import { type ClientId } from '../client/bridge-api-client-types';

import { type RPCResponse } from './bridge-api-rpc-message';

/**
 * An envelope representing a deferred promise. It is resolved or rejected after a remote client (the {@link receiverId})
 * handles a command sent by the sender ({@link senderId}). The promise is resolved or rejected based on the {@link RPCResponse}.
 */
export type DeferredResult<T> = {
	/**
	 * ID of the original callee.
	 */
	senderId: ClientId;
	/**
	 * ID of the client expected to handle this request.
	 */
	receiverId: ClientId;
	/**
	 * Deferred promise resolve handler, which evaluates to a provided T.
	 */
	resolve: (value: T) => void;
	/**
	 * Deferred promise reject handler, which evalues to an error.
	 */
	reject: (reason?: any) => void;
	/**
	 * Higher-level promise which contains {@link resolve} and {@link reject} in its scope.
	 */
	promise: Promise<T>;
};

/**
 * Maintains all asynchronous, deferred state for delegated RPC method calls.
 *
 * I.e., if JS context 1 invokes a command which is handled by JS context 2, {@link ResponseHandler} is
 * responsible for ensuring the result passed back by JS context 2 is routed to the correct promise being
 * awaited by JS context 1. Streamlines handling of method calls over JS contexts.
 */
export class ResponseHandler {
	/**
	 * A mapping from unique command invocations to deferred promises waiting
	 * for their result. Used for match-making the result of a delegated RPC call
	 * back to its callee. TODO - hook up to methods in CNS-18261.
	 */
	private responses = new Map<string, DeferredResult<any>>();

	/**
	 * Creates a deferred promise handler for a given command invocation.
	 * @param id unique ID for the command invocation
	 * @param senderId the callee
	 * @param receiverId the receiver which implements a handler for the command
	 * @returns a deferrable which is resolved upon a response being yielded by the receiver
	 */
	public getOrCreate<T>(id: string, senderId: ClientId, receiverId: ClientId): Promise<T> {
		// if we already have a deferred promise waiting for this specific invocation, we do not need another one.
		const existing = this.get<T>(id);
		if (existing) {
			return existing.promise;
		}

		let deferredResolve: (value: T) => void;
		let deferredReject: (reason?: any) => void;

		const promise = new Promise<T>((resolve, reject) => {
			deferredResolve = resolve;
			deferredReject = reject;
		});

		const result: DeferredResult<T> = {
			senderId,
			receiverId,
			promise,
			// @ts-expect-error - Promise executor runs synchronously so deferredResolve is guaranteed assigned; TypeScript cannot infer this
			resolve: deferredResolve,
			// @ts-expect-error - Promise executor runs synchronously so deferredReject is guaranteed assigned; TypeScript cannot infer this
			reject: deferredReject,
		};

		this.responses.set(id, result);
		return promise;
	}

	/**
	 * Yields the current deferrable state for a command invocation.
	 * @param id unique ID for the command invocation.
	 * @returns deferrable state for a command invocation.
	 */
	public get<T>(id: string): DeferredResult<T> | undefined {
		return this.responses.get(id);
	}

	/**
	 * Resolves a promise reference based upon the ID of an RPC response.
	 *
	 * @description If the RPC response is successful, will resolve the promise with the expected return shape.
	 * @description If the RPC response is unsuccessful, will reject the promise with an error.
	 *
	 * @param _response response yielded by the receiver of the original message.
	 */
	public resolveWithResponse(response: RPCResponse): void {
		const id = response.id;
		const deferred = this.responses.get(id);

		// no one waiting - exit
		if (!deferred) {
			return;
		}

		// drop the deferrable from storage now
		this.responses.delete(id);

		// yield correct outcome
		if (response.error) {
			deferred.reject(new Error(response.error.message));
		} else {
			deferred.resolve(response.result);
		}
	}

	/**
	 * Cancels a specific promise by resolving it to undefined to avoid unhandled rejections
	 * in fire-and-forget scenarios.
	 *
	 * @description In JavaScript if you do the following:
	 * ```typescript
	 * const promise = () => new Promise((_, reject) => reject(new Error('oh boy')));
	 * promise();
	 * ```
	 * Even though you are not explicitly calling `await` on the result of the promise, it will
	 * reject and result in an unhandled rejection. Specifically when working with commands, we
	 * often want to send them in a fire-and-forget manner. When a 'cancellation' takes place,
	 * we therefore DO NOT want an unhandled rejection to result. Hence, we treat all command
	 * results similar to distributed system communications - as always nullable.
	 *
	 * Cancellation events to consider: component unmounts, route transitions.
	 *
	 * For more, see:
	 * - {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event}
	 * - {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Using_promises#promise_rejection_events}
	 * - {@link https://typescript-eslint.io/rules/no-floating-promises/}
	 *
	 * @param id unique ID for the command invocation to cancel
	 */
	public cancel(id: string): void {
		const deferred = this.responses.get(id);
		if (!deferred) {
			return;
		}

		// clear out in-memory reference
		this.responses.delete(id);

		// resolve to undefined to avoid unhandled promise rejections
		deferred.resolve(undefined);
	}

	/**
	 * Clears response handlers for a specific client to prevent memory leaks during client teardown.
	 *
	 * @param clientId The client ID to clear responses for (as sender or receiver)
	 */
	public teardown(clientId: ClientId): void {
		const toDelete: string[] = [];

		for (const [id, deferred] of this.responses) {
			if (deferred.senderId === clientId || deferred.receiverId === clientId) {
				// Resolve to undefined to avoid unhandled promise rejections
				deferred.resolve(undefined);
				toDelete.push(id);
			}
		}

		for (const id of toDelete) {
			this.responses.delete(id);
		}
	}

	/**
	 * Destroy all relevant state.
	 */
	public clear(): void {
		// Resolve all pending promises to avoid unhandled rejections
		for (const deferred of this.responses.values()) {
			deferred.resolve(undefined);
		}

		this.responses.clear();
	}
}
